/**
 * Bed Category catalog API (B2 / S1–S3) — IPD tariff masters.
 *
 * Backend endpoints (org resolved server-side from the session; every route is
 * behind B1's `require_admission_enabled` gate + `MANAGE_BED_CONFIG`):
 *
 *   POST   /api/organisation/bed-categories
 *   GET    /api/organisation/bed-categories?include_inactive=
 *   GET    /api/organisation/bed-categories/:bed_category_id
 *   PATCH  /api/organisation/bed-categories/:bed_category_id
 *   DELETE /api/organisation/bed-categories/:bed_category_id   (soft delete)
 *
 * Clone of procedureApi.js MINUS the A4 dept-scoping machinery (catalogs are
 * ORG-GLOBAL — no /departments endpoint, no department params). Every function
 * returns `{ success, data?, error?, code? }`; `code` carries the server's
 * machine-readable business-rule code when the 4xx `detail` is a
 * `{code, message}` dict (e.g. RATE_EFFECTIVE_FROM_IN_PAST, ADMISSION_DISABLED).
 */

import apiClient from './apiClient';

const BASE = '/api/organisation/bed-categories';

/**
 * Translate an axios error into `{ error, code? }`.
 * - dict detail `{code, message}` → user message + machine code (R-B2-5)
 * - string detail → verbatim server message
 * - Pydantic list detail / anything else → status-specific friendly fallback
 */
export function parseCatalogError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    return { error: detail.message || fallback, code: detail.code };
  }
  if (typeof detail === 'string') return { error: detail };

  switch (error?.response?.status) {
    case 400: return { error: 'Invalid data — please check the form and try again.' };
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to perform this action.' };
    case 404: return { error: 'The requested item could not be found.' };
    case 409: return { error: 'That item already exists. Pick a different name.' };
    case 422: return { error: 'Invalid data — please check the form and try again.' };
    case 500: return { error: 'Server error. Please try again in a moment.' };
    default:  return { error: fallback };
  }
}

/** POST /api/organisation/bed-categories → 201 BedCategoryResponse */
export const createBedCategory = async (body) => {
  try {
    const response = await apiClient.post(BASE, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedCategoryAPI] create error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to create bed category.') };
  }
};

/**
 * GET /api/organisation/bed-categories → {bed_categories:[...], count}
 * First GET on an IPD-ON org lazily seeds the presets server-side.
 */
export const listBedCategories = async ({ include_inactive } = {}) => {
  try {
    const params = {};
    if (include_inactive) params.include_inactive = true;
    const response = await apiClient.get(BASE, { params });
    return {
      success: true,
      data: {
        bed_categories: response.data?.bed_categories || [],
        count: response.data?.count ?? (response.data?.bed_categories?.length || 0),
      },
    };
  } catch (error) {
    console.error('[BedCategoryAPI] list error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to load bed categories.') };
  }
};

/** GET /api/organisation/bed-categories/:id */
export const getBedCategory = async (bed_category_id) => {
  try {
    const response = await apiClient.get(`${BASE}/${encodeURIComponent(bed_category_id)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedCategoryAPI] get error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to load bed category.') };
  }
};

/**
 * PATCH /api/organisation/bed-categories/:id — partial update. A payload
 * carrying any charge field appends a new effective-dated rate_history entry
 * server-side (non-retroactive, S2).
 */
export const updateBedCategory = async (bed_category_id, body) => {
  try {
    const response = await apiClient.patch(`${BASE}/${encodeURIComponent(bed_category_id)}`, body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedCategoryAPI] update error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to update bed category.') };
  }
};

/**
 * B5 / S2 — tag a bed category with 0..N departments ("Visible to").
 * Empty array clears all edges → owner_scope reverts to ORG_WIDE; ≥1 →
 * owner_scope=DEPARTMENT. Server rejects (422 `{code, message}`) an unknown
 * (DEPARTMENT_NOT_FOUND) / inactive (DEPARTMENT_INACTIVE) / racing
 * (CONCURRENT_MODIFICATION) department. Response = enriched BedCategoryResponse.
 *
 * PUT /api/organisation/bed-categories/:id/departments
 * @param {string}   bed_category_id
 * @param {string[]} department_ids
 * @returns {Promise<{success, data?, error?, code?}>}
 */
export const setBedCategoryDepartments = async (bed_category_id, department_ids) => {
  try {
    const response = await apiClient.put(
      `${BASE}/${encodeURIComponent(bed_category_id)}/departments`,
      { department_ids: department_ids || [] }
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedCategoryAPI] setDepartments error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to save departments.') };
  }
};

/** DELETE /api/organisation/bed-categories/:id → 200 (soft-delete, active=false) */
export const deactivateBedCategory = async (bed_category_id) => {
  try {
    const response = await apiClient.delete(`${BASE}/${encodeURIComponent(bed_category_id)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[BedCategoryAPI] deactivate error:', error);
    return { success: false, ...parseCatalogError(error, 'Failed to deactivate bed category.') };
  }
};
